"use client";

// Tremor Dialog [v1.0.0]
import * as DialogPrimitives from "@radix-ui/react-dialog";

import React from "react";

import { ScrollShadow } from "@/components/ui/scroll-shadow";

import { cx, focusRing } from "@/lib/tailwind-utils";

const Dialog = (
  props: React.ComponentPropsWithoutRef<typeof DialogPrimitives.Root>,
) => {
  return <DialogPrimitives.Root {...props} />;
};
Dialog.displayName = "Dialog";

const DialogTrigger = DialogPrimitives.Trigger;

DialogTrigger.displayName = "DialogTrigger";

const DialogClose = DialogPrimitives.Close;

DialogClose.displayName = "DialogClose";

const DialogPortal = DialogPrimitives.Portal;

DialogPortal.displayName = "DialogPortal";

const DialogOverlay = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitives.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitives.Overlay>
>(({ className, children, ...props }, forwardedRef) => {
  return (
    <DialogPrimitives.Overlay
      ref={forwardedRef}
      className={cx(
        // base
        "fixed inset-0 z-50 overflow-y-auto",
        // transition
        "data-[state=open]:animate-dialog-overlay-show",
        className,
      )}
      {...props}
    >
      <div className="fixed inset-0 bg-background/15 force-transparent backdrop-blur-[1px]" />
      {children}
    </DialogPrimitives.Overlay>
  );
});

DialogOverlay.displayName = "DialogOverlay";

const DialogContent = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitives.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitives.Content>
>(({ className, children, ...props }, forwardedRef) => {
  return (
    <DialogPortal>
      <DialogOverlay>
        <DialogPrimitives.Content
          ref={forwardedRef}
          className={cx(
            // base
            "fixed left-1/2 z-50 flex w-[95vw] max-w-lg -translate-x-1/2 -translate-y-1/2 flex-col rounded-xl border border-border p-1 shadow-2xl motion-duration-100",
            // Centred in, and no taller than, the screen above an on-screen keyboard
            // (--keyboard-inset; unset otherwise), so its fields can scroll into view.
            "top-[calc((100dvh-var(--keyboard-inset,0px))/2)] max-h-[calc(90dvh-var(--keyboard-inset,0px))]",
            // The same cap again while a keyboard is up, as a variant so a dialog's own
            // max-h (the face-chart annotator's 95vh) can't drop it. Without it a tall
            // dialog stays tall while its centre moves up, and its top leaves the screen.
            "[html[data-keyboard-open]_&]:max-h-[calc(100dvh-var(--keyboard-inset,0px)-1rem)]",
            // background color
            "bg-background/85 backdrop-blur-3xl",
            // transition
            "origin-top-left motion-scale-in-75",
            focusRing,
            className,
          )}
          tremor-id="tremor-raw"
          {...props}
        >
          {children}
          <div className="shadow-inner pointer-events-none absolute inset-0 rounded-xl" />
        </DialogPrimitives.Content>
      </DialogOverlay>
    </DialogPortal>
  );
});

DialogContent.displayName = "DialogContent";

const DialogHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { elevated?: boolean }
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cx("flex flex-col gap-y-1 p-4 pb-4 md:p-6 md:pb-4", className)}
      {...props}
    />
  );
});

DialogHeader.displayName = "DialogHeader";

const DialogTitle = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitives.Title>
>(({ className, ...props }, forwardedRef) => (
  <DialogPrimitives.Title
    ref={forwardedRef}
    className={cx(
      // base
      "text-h3",
      // text color
      "text-foreground",
      className,
    )}
    {...props}
  />
));

DialogTitle.displayName = "DialogTitle";

const DialogDescription = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitives.Description>
>(({ className, ...props }, forwardedRef) => {
  return (
    <DialogPrimitives.Description
      ref={forwardedRef}
      className={cx("text-muted-foreground", className)}
      {...props}
    />
  );
});

DialogDescription.displayName = "DialogDescription";

const DialogBody = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => {
  return (
    <ScrollShadow.Root>
      <ScrollShadow.Viewport
        ref={ref}
        className={cx("px-4 py-4 pt-0 md:px-6 md:pt-0", className)}
        {...props}
      >
        {children}
      </ScrollShadow.Viewport>
      <ScrollShadow.Top />
      <ScrollShadow.Bottom />
    </ScrollShadow.Root>
  );
});

DialogBody.displayName = "DialogBody";

const DialogFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cx(
        "flex flex-row items-center justify-end space-x-2 p-4 md:p-6 md:pt-4",
        className,
      )}
      {...props}
    />
  );
});

DialogFooter.displayName = "DialogFooter";

export {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
};
